/**
 * Rewrites the granule positions of an Ogg/Opus stream so they match the audio it contains.
 *
 * Why this exists: the ffmpeg.wasm core we load (@ffmpeg/core, libavformat 59.27.100) misstates
 * granule positions by 24 samples on some pages of what MediaRecorder now produces. WhatsApp
 * Cloud checks every page, so a single wrong one rejects the file with error 131053
 * ("...however on processing it is of type application/octet-stream") — even though every
 * lenient decoder plays it fine. Recordings made before the browser's recorder changed had
 * correct granules and were accepted, which is why sending voice notes broke without any
 * change on our side.
 *
 * Verified by sending one rejected recording twice: untouched it failed, and with only these
 * granule bytes repaired (68 of 259,248) it was delivered and read.
 */

const OGG_MAGIC = 0x4f676753; // "OggS"
const HEADER_CRC_OFFSET = 22;
const SEGMENT_COUNT_OFFSET = 26;

const CRC_TABLE = (() => {
	const table = new Uint32Array(256);
	for (let i = 0; i < 256; i++) {
		let r = i << 24;
		for (let bit = 0; bit < 8; bit++) {
			r = r & 0x80000000 ? ((r << 1) ^ 0x04c11db7) >>> 0 : (r << 1) >>> 0;
		}
		table[i] = r >>> 0;
	}
	return table;
})();

const oggCrc = (bytes, start, end) => {
	let crc = 0;
	for (let i = start; i < end; i++) {
		crc = ((crc << 8) ^ CRC_TABLE[((crc >>> 24) ^ bytes[i]) & 0xff]) >>> 0;
	}
	return crc >>> 0;
};

/** Samples per frame at 48 kHz, from the packet's TOC byte (RFC 6716 §3.1). */
const frameSamples = (toc) => {
	const config = toc >> 3;
	let ms;
	if (config < 12) ms = [10, 20, 40, 60][config % 4];
	else if (config < 16) ms = [10, 20][config % 2];
	else ms = [2.5, 5, 10, 20][config % 4];
	return Math.round(ms * 48);
};

/** Frame count in the packet, from its TOC code (RFC 6716 §3.2). */
const frameCount = (packet) => {
	const code = packet[0] & 3;
	if (code === 0) return 1;
	if (code === 1 || code === 2) return 2;
	return packet[1] & 0x3f;
};

/** Splits a page body into packets using the lacing table. */
const splitPackets = (body, lacing) => {
	const packets = [];
	let offset = 0;
	let length = 0;
	for (const segment of lacing) {
		length += segment;
		if (segment < 255) {
			if (length > 0) packets.push(body.subarray(offset, offset + length));
			offset += length;
			length = 0;
		}
	}
	return packets;
};

/** Walks the pages once, reporting each audio page and the sample count decoded through it. */
const eachAudioPage = (bytes, view, visit) => {
	let offset = 0;
	let samples = 0;

	while (offset + 27 <= bytes.length) {
		if (view.getUint32(offset) !== OGG_MAGIC) break;

		const segmentCount = bytes[offset + SEGMENT_COUNT_OFFSET];
		const lacing = bytes.subarray(offset + 27, offset + 27 + segmentCount);
		const headerLength = 27 + segmentCount;
		let bodyLength = 0;
		for (const segment of lacing) bodyLength += segment;

		const pageEnd = offset + headerLength + bodyLength;
		if (pageEnd > bytes.length) break;

		const body = bytes.subarray(offset + headerLength, pageEnd);
		// OpusHead / OpusTags both start with "Opus" and carry a granule of 0.
		const isHeaderPage =
			body.length >= 8 && body[0] === 0x4f && body[1] === 0x70 && body[2] === 0x75 && body[3] === 0x73;

		if (!isHeaderPage) {
			for (const packet of splitPackets(body, lacing)) {
				if (packet.length > 0) samples += frameCount(packet) * frameSamples(packet[0]);
			}
			if (visit(offset, pageEnd, samples) === false) return;
		}

		offset = pageEnd;
	}
};

export const fixOggOpusGranules = (input) => {
	const source = input instanceof Uint8Array ? input : new Uint8Array(input);
	const probe = new DataView(source.buffer, source.byteOffset, source.byteLength);

	// The drift is not a constant offset — it lands on scattered pages — so every page has to be
	// checked. WhatsApp validates each one, and a single mismatched page rejects the whole file.
	const pages = [];
	eachAudioPage(source, probe, (offset, pageEnd, samples) => {
		pages.push({ offset, pageEnd, samples });
	});
	if (pages.length === 0) return source;

	// The final page deliberately reports fewer samples than its packets carry — that is how Opus
	// signals end trimming — so it keeps its value. Every earlier page must state the exact count.
	const stale = pages
		.slice(0, -1)
		.filter((page) => Number(probe.getBigUint64(page.offset + 6, true)) !== page.samples);
	if (stale.length === 0) return source;

	const bytes = source.slice();
	const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
	const rewrite = (page, granule) => {
		view.setBigUint64(page.offset + 6, BigInt(granule), true);
		view.setUint32(page.offset + HEADER_CRC_OFFSET, 0, true);
		view.setUint32(page.offset + HEADER_CRC_OFFSET, oggCrc(bytes, page.offset, page.pageEnd), true);
	};

	for (const page of stale) rewrite(page, page.samples);

	// Guard against a correction overtaking the trimmed final page, which would leave the granule
	// positions running backwards.
	const last = pages[pages.length - 1];
	const previous = pages.length > 1 ? Number(view.getBigUint64(pages[pages.length - 2].offset + 6, true)) : 0;
	if (Number(view.getBigUint64(last.offset + 6, true)) < previous) rewrite(last, last.samples);

	return bytes;
};
